import { Injectable } from '@angular/core';
import { throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ApiResponse } from '../../types/api-response.type';
import {
  EnhancedLogoNode,
  Logo,
  LogoApiResponse,
  LogoFile,
} from '../../types/logo.type';
import { ApiService } from '../api/api.service';

@Injectable({
  providedIn: 'root',
})
export class LogoService extends ApiService<Logo> {
  constructor() {
    super();
    this.moduleUrl.set('/jsonapi/node/logo');
  }

  getLogos(companyId?: string) {
    const params = companyId
      ? `?include=field_imagen&filter[field_empresa]=${companyId}`
      : `?include=field_imagen`;

    return this.http.get<LogoApiResponse>(`${this.url()}${params}`).pipe(
      map((response) => {
        const fileMap = new Map<string, LogoFile>();
        if (response.included) {
          response.included.forEach((file) => {
            if (file.type === 'file--file') {
              fileMap.set(file.id, file);
            }
          });
        }

        const enhancedLogoNodes: EnhancedLogoNode[] = response.data.map(
          (logoNode) => {
            const fileId = logoNode.relationships?.field_imagen?.data?.id;
            const populatedFile = fileId ? fileMap.get(fileId) : undefined;

            if (!fileId) {
              console.warn(`⚠️ Logo ${logoNode.id} has no file relationship`);
            } else if (!populatedFile) {
              console.error(
                `❌ Logo ${logoNode.id} references file ${fileId} but file not found in included data`,
              );
            }

            return {
              ...logoNode,
              populatedFile,
            };
          },
        );
        return enhancedLogoNodes;
      }),
    );
  }

  uploadLogo(file: File) {
    const headers = {
      'Content-Type': 'application/octet-stream',
      Accept: 'application/vnd.api+json',
      'Content-Disposition': `file; filename="${file.name}"`,
    };

    return this.http.post<ApiResponse<Logo>>(
      `${this.url()}/field_imagen`,
      file,
      { headers },
    );
  }

  createLogoNode(
    imageId: string,
    companyId: string | undefined,
    title: string,
  ) {
    const body = {
      data: {
        type: 'node--logo',
        attributes: {
          title,
          field_empresa: companyId,
        },
        relationships: {
          field_imagen: {
            data: {
              type: 'file--file',
              id: imageId,
            },
          },
        },
      },
    };

    return this.http
      .post<ApiResponse<Logo>>(this.url(), body, {
        headers: {
          'Content-Type': 'application/vnd.api+json',
        },
      })
      .pipe(map((response) => response.data));
  }

  addLogo(file: File, companyId?: string) {
    return this.uploadLogo(file).pipe(
      switchMap((response) =>
        this.createLogoNode(response.data.id, companyId, file.name),
      ),
    );
  }

  updateLogoNode(
    idNode: string,
    fileUuid: string,
    companyId: string | undefined,
    updatedTitle: string,
  ) {
    const body = {
      data: {
        type: 'node--logo',
        id: idNode,
        attributes: {
          title: updatedTitle,
          field_empresa: {
            value: companyId,
          },
        },
        relationships: {
          field_imagen: {
            data: {
              type: 'file--file',
              id: fileUuid,
            },
          },
        },
      },
    };

    return this.http
      .patch<ApiResponse<Logo>>(`${this.url()}/${idNode}`, body, {
        headers: {
          'Content-Type': 'application/vnd.api+json',
        },
      })
      .pipe(
        catchError((error) => {
          console.error('❌ PATCH Error:', {
            status: error.status,
            statusText: error.statusText,
            message: error.message,
            url: error.url,
            error: error.error,
          });
          return throwError(() => error);
        }),
        map((response) => response.data),
      );
  }

  updateLogo(file: File, idNode: string, companyId?: string) {
    return this.uploadLogo(file).pipe(
      switchMap((response) =>
        this.updateLogoNode(idNode, response.data.id, companyId, file.name),
      ),
    );
  }

  findExistingLogo(companyId: string) {
    return this.getLogos(companyId).pipe(
      map((enhancedLogos) => {
        const logoWithMatchingFile = enhancedLogos.find((logo) => {
          const imageId = logo.relationships?.field_imagen?.data?.id;
          return imageId && logo.populatedFile;
        });

        return logoWithMatchingFile ?? null;
      }),
    );
  }

  addOrUpdateLogo(file: File, companyId?: string) {
    if (!companyId) {
      return throwError(
        () => new Error('Company ID is required for logo upload'),
      );
    }

    return this.findExistingLogo(companyId).pipe(
      switchMap((existingLogo) => {
        if (existingLogo) {
          return this.updateLogo(file, existingLogo.id, companyId);
        } else {
          return this.addLogo(file, companyId);
        }
      }),
    );
  }
}
